"use client";

import type { PricingPlan } from "@/components/landing/Pricing/types";
import { MotionReveal, SectionHeader, useSectionMotion } from "@/components/landing/shared";
import { cn } from "@/lib/utils";

interface PricingCardsProps {
	title: string;
	intro: string;
	popularLabel: string;
	plans: PricingPlan[];
}

/**
 * Split out from `Pricing/index.tsx` because that file's default export is an
 * async Server Component (it fetches plans from `centralPrisma`), and async
 * components cannot be Client Components — but `useSectionMotion()` requires
 * one. This is the actual entrance-animated body, rendered as the child of
 * `SectionShell` in `index.tsx`.
 */
function PricingCards(prop: PricingCardsProps) {
	const { title, intro, popularLabel, plans } = prop;
	const motion = useSectionMotion();
	return (
		<div className="mx-auto max-w-6xl px-6">
			<MotionReveal motion={motion}>
				<SectionHeader
					title={title}
					subtitle={intro}
					wrapperClassName="mx-auto mb-12 max-w-2xl text-center"
					subtitleClassName="mt-3 text-muted-foreground"
				/>
			</MotionReveal>
			<div className="grid gap-5 md:grid-cols-3">
				{plans.map((plan, i) => (
					<MotionReveal motion={motion} delayOffset={i * 80} key={plan.name}>
						<article
							className={cn("card relative h-full p-6", plan.popular ? "ring-2 ring-primary" : "")}
						>
							{plan.popular && (
								<span className="badge absolute end-4 top-4 bg-primary text-primary-foreground">
									{popularLabel}
								</span>
							)}
							<h3 className="pe-24 text-xl font-bold">{plan.name}</h3>
							{plan.description && (
								<p className="mt-3 min-h-12 text-sm leading-relaxed text-muted-foreground">
									{plan.description}
								</p>
							)}
							<div className="mt-6 flex items-end gap-2">
								<strong className="text-4xl">{plan.price}</strong>
								<span className="pb-1 text-sm text-muted-foreground">{plan.interval}</span>
							</div>
							<a href="#contact" className="btn btn-primary mt-6 w-full">
								{plan.cta}
							</a>
						</article>
					</MotionReveal>
				))}
			</div>
		</div>
	);
}

export default PricingCards;
